import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { InlineTabs } from '@soteria/design-system-ve';
import type { ReactNode } from 'react';
import { useState } from 'react';

export interface TabConfig {
  key: string;
  label: string;
  content: ReactNode;
  disabled?: boolean;
  disabledTooltip?: string;
}

interface TabNavigationProps {
  tabs: TabConfig[];
  defaultActiveTab?: string;
  ariaLabel: string;
  onTabChange?: (activeTabKey: string) => void;
}

function TabNavigationComponent({ tabs, defaultActiveTab, ariaLabel, onTabChange }: TabNavigationProps) {
  const [activeTab, setActiveTab] = useState<string>(defaultActiveTab || tabs[0]?.key || '');

  const tabLabels = tabs.map(tab => ({
    label: tab.label,
    ...(tab.disabled && tab.disabledTooltip && { title: tab.disabledTooltip }),
  }));
  const tabKeys = tabs.map(tab => tab.key);

  const activeTabIndex = tabKeys.indexOf(activeTab);

  const handleTabChange = (index: number) => {
    const newTabKey = tabKeys[index];
    const targetTab = tabs.find(tab => tab.key === newTabKey);

    // Prevent switching to disabled tabs
    if (targetTab?.disabled) {
      return;
    }

    setActiveTab(newTabKey);
    onTabChange?.(newTabKey);
  };

  const renderTabContent = () => {
    const activeTabConfig = tabs.find(tab => tab.key === activeTab);

    return activeTabConfig?.content || null;
  };

  return (
    <div>
      {/* Tab Navigation */}
      <div style={{ marginBottom: '24px' }}>
        <InlineTabs aria-label={ariaLabel} size="medium" tabs={tabLabels} value={activeTabIndex} variant="default" onValueChange={handleTabChange} />
      </div>

      {/* Tab Content */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '48px' }}>{renderTabContent()}</div>
    </div>
  );
}

export const TabNavigation = withAppInsightsTracking(TabNavigationComponent, 'TabNavigation');
